import { Button } from 'components/atoms';
import LabelReportButtonDownload from 'components/molecules/buttons/reportDownload/LabelReportButtonDownload';
import PasswordProtectButton from 'components/molecules/buttons/passwordProtect/PasswordProtectButton';
import Popup from 'components/molecules/popup';
import { Form } from 'components/organisms';
import {
  initialLabelReportDetail,
} from 'components/organisms/forms/label-report/LabelReportForm';
import { useCustomParamId } from 'hooks/useParamId';
import { useStoreState } from 'providers/StoreStateContext';
import { PiTrash } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useDeleteReport,
  useGetLabelReport,
  usePasswordProtectLabelReport,
  useSetLabelReportAsUpToDate,
} from 'services/label-report';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const LabelReport = () => {
  const i18nextPrefix = 'label-reports';
  const reportId = useCustomParamId('reportId', '/label-reports');
  const labelId = useCustomParamId('id');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useGetLabelReport(reportId);
  const deleteReport = useDeleteReport();
  const passwordProtectLabelReport = usePasswordProtectLabelReport();
  const { applicationSettings } = useStoreState();
  const isPdfPasswordProtectionEnabled =
    applicationSettings?.enablePdfPasswordProtection ?? false;
  const { mutate: setLabelReportAsUpToDate, isPending: isMarkingAsUpToDate } =
    useSetLabelReportAsUpToDate();

  const initialValues = data
    ? {
        ...data,
        periodFromDisplay: getSettlementPeriodName(
          data.startSettlementPeriodName
        ),
        periodToDisplay: getSettlementPeriodName(data.endSettlementPeriodName),
        amountEarnedDisplay: formatCurrency(
          data.amountEarned != null ? Number(data.amountEarned) : 0
        ),
        amountForLabelDisplay: formatCurrency(
          data.amountForLabel != null ? Number(data.amountForLabel) : 0
        ),
        amountForOwnerDisplay: formatCurrency(
          data.amountForOwner != null ? Number(data.amountForOwner) : 0
        ),
      }
    : initialLabelReportDetail;

  const finalBackPath =
    location.state === 'raport'
      ? 'raport'
      : labelId
        ? `label/${labelId}/reports`
        : 'raport';

  const onDeleteReport = () => {
    deleteReport.mutateAsync(reportId, {
      onSuccess: () => {
        navigate(`/${finalBackPath}`, { replace: true });
      },
    });
  };

  const onMarkAsUpToDate = () => {
    setLabelReportAsUpToDate(
      { labelReportId: reportId },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  const onPasswordProtect = () => {
    passwordProtectLabelReport.mutate(
      { labelReportId: reportId },
      {
        onSuccess: () => {
          refetch();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  return (
    <Form.LabelReport
      i18nextPrefix={i18nextPrefix}
      initialValues={initialValues}
      topbarContent={
        data && (
          <div className='flex gap-1'>
            {data.isOutdated && (
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={onMarkAsUpToDate}
                disabled={isMarkingAsUpToDate}
              >
                <Button.Text text={t('button.mark-as-up-to-date')} />
              </Button>
            )}
            <PasswordProtectButton
              visible={
                isPdfPasswordProtectionEnabled &&
                ((!!data.filePath && !data.isPasswordProtected) ||
                  (!!data.detailedFilePath &&
                    !data.isDetailedFilePasswordProtected))
              }
              isLoading={passwordProtectLabelReport.isPending}
              onClick={onPasswordProtect}
            />
            <LabelReportButtonDownload
              id={reportId}
              name={data.labelReportNumber ?? data.labelName ?? ''}
              canGenerateDetailedReport={data.canGenerateDetailedReport}
              isPasswordProtected={data.isPasswordProtected}
              isDetailedFilePasswordProtected={
                data.isDetailedFilePasswordProtected
              }
            />
            <Popup.DeleteWarning
              name={data.labelReportNumber || data.labelName || t('label-reports.what')}
              onRemove={onDeleteReport}
              trigger={
                <Button variant={BUTTON_VARIANT.remove} onlyIcon isClear>
                  <Button.Description description={t('button.remove')} />


                  <Button.Icon icon={<PiTrash />} />
                </Button>
              }
            />
          </div>
        )
      }
      onSubmit={() => {}}
      tittle={data?.labelReportNumber}
      status={{ displayMode: true, isLoading }}
      backPath={finalBackPath}
    />
  );
};

export default LabelReport;
